// Job Details Content for Modal
// Adapted from Details.web.jsx - main content only (no sidebar)

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
  ActivityIndicator,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { getJob, getJobFacilities } from '../../services/job';
import { getOverview } from '../../services/profile';
import { webColors, webBorderRadius, webSpacing } from '../../constants/webStyles';
import { formatPrice, formatPriceRange, getJobDisplayCurrency } from '../../utils/currency';
import { jobTypes, isFullTimeJob } from '../../constants/lov/professions';
import { useIsMobile } from '../../hooks/useIsMobile';

const DetailsContent = ({ job: initialJob }) => {
  const { country: userCountry, currencyPreset } = useSelector((state) => state.user);
  const isMobile = useIsMobile();

  // Fetch job details
  const {
    data: jobQuery,
    isLoading: jobLoading,
  } = useQuery({
    queryKey: ['jobDetailsModal', initialJob?.uuid],
    queryFn: () => getJob(initialJob?.uuid),
    enabled: !!initialJob?.uuid,
  });
  const job = jobQuery?.data?.data || initialJob;

  // Fetch job facilities/clinical expertise
  const {
    data: jobFacilitiesQuery,
    isLoading: facilitiesLoading,
  } = useQuery({
    queryKey: ['jobFacilitiesModal', initialJob?.uuid],
    queryFn: () => getJobFacilities(initialJob?.uuid),
    enabled: !!initialJob?.uuid,
  });
  const jobFacilityData = jobFacilitiesQuery?.data?.data || [];

  // Fetch overview data for city/state
  const { data: overviewData } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
  });
  const clinicCity = overviewData?.data?.data?.city;
  const clinicState = overviewData?.data?.data?.state;

  const isFullTime = isFullTimeJob(job?.type);
  const displayCurrency = getJobDisplayCurrency(job ?? initialJob, initialJob?.country || currencyPreset || userCountry);

  // Calculate hours difference
  const diffHours = Math.abs(
    moment(job?.start_timestamp).diff(moment(job?.end_timestamp), 'hours') || 1
  );

  const isLoading = jobLoading;

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={webColors.primary} />
      </View>
    );
  }

  if (!job) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Job details not available</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Job Header */}
      <View style={styles.jobHeader}>
        <View style={[styles.companyLogo, { backgroundColor: getColorFromName(job.clinic?.name) }]}>
          <Text style={styles.companyLogoText}>{job.clinic?.name?.[0] || 'C'}</Text>
        </View>
        <View style={styles.jobHeaderInfo}>
          {job.is_emergency && (
            <Text style={styles.urgentLabel}>URGENT JOB</Text>
          )}
          {job.subtitle && (
            <View style={styles.subtitlePill}>
              <Text style={styles.subtitlePillText}>{job.subtitle}</Text>
            </View>
          )}
          <Text style={styles.companyName}>{job.clinic?.name || 'Healthcare Facility'}</Text>
          <Text style={styles.jobTitle}>{job.title}</Text>
          <View style={[styles.jobMeta, isMobile && styles.jobMetaMobile]}>
            <Text style={styles.metaText}>Posted {moment(job.created_at).fromNow()}</Text>
            <Text style={styles.metaDot}>-</Text>
            <Text style={styles.metaText}>{[job.clinic?.city || job.city || clinicCity, job.clinic?.state || job.state || clinicState].filter(Boolean).join(', ') || 'Location TBD'}</Text>
          </View>
        </View>
      </View>

      {/* Tags */}
      <View style={styles.tagsRow}>
        <View style={styles.jobTypeTag}>
          <Text style={styles.jobTypeTagText}>
            {job.international ? 'International' : jobTypes.find(type => type.id === parseInt(job.type))?.name || 'Locum'}
          </Text>
        </View>
        {!job.is_cash && (
          <View style={styles.spayTag}>
            <Text style={styles.spayTagText}>S-PAY</Text>
          </View>
        )}
        {job.is_cash && (
          <View style={styles.cashTag}>
            <Text style={styles.cashTagText}>CASH</Text>
          </View>
        )}
      </View>

      {/* About the job */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>About the job</Text>
        <Text style={styles.descriptionText}>
          {job.description || 'No description provided.'}
        </Text>
      </View>

      {/* Salary/Earnings Section */}
      {isFullTime ? (
        job.price > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Job Salary</Text>
            <Text style={styles.salaryText}>
              {formatPriceRange(job.price, job.price_max, displayCurrency)} per month
            </Text>
          </View>
        )
      ) : (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Earnings</Text>
          <Text style={styles.earningsHighlight}>
            {formatPrice((job.price / diffHours).toFixed(2), displayCurrency)} per hour
          </Text>
          <Text style={styles.earningsTotal}>
            Total: {formatPrice(job.price, displayCurrency)} ({diffHours} hours)
          </Text>
          <View style={[styles.paymentTag, { borderColor: !job.is_cash ? '#FC6767' : 'grey' }]}>
            <Text style={[styles.paymentTagText, { color: !job.is_cash ? '#FC6767' : 'grey' }]}>
              {job.is_cash ? 'CASH' : 'S-PAY'}
            </Text>
          </View>
          <Text style={styles.paymentNote}>
            {job.is_cash
              ? 'Total shift pay will be made to you by the facility, you are required to liaise directly with the respective facility. T&C apply.'
              : "Total shift pay will be credited directly into your bank account from Supermedic. You're eligible for the following: Next-day pay | Immediate shift coverage. T&C apply."}
          </Text>
        </View>
      )}

      {/* Clinical Expertise Section (for locum jobs) */}
      {!isFullTime && jobFacilityData?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Clinical Expertise Required</Text>

      
            <View style={styles.expertiseGroup}>
                <View style={styles.listItem}>
                  <Text style={styles.listBullet}>-</Text>
                  <Text style={styles.listText}>{job.title}</Text>
                </View>
            </View>
        </View>
      )}

      {/* Date & Time Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{isFullTime ? 'Application Deadline' : 'Date & Time'}</Text>
        {moment(job.start_timestamp).format('DD') !== moment(job.end_timestamp).format('DD') ? (
          <View style={[styles.dateRange, isMobile && styles.dateRangeMobile]}>
            <View style={styles.dateItem}>
              <Text style={styles.dateMain}>{moment(job.start_timestamp).format('MMMM Do')}</Text>
              {!isFullTime && (
                <Text style={styles.dateTime}>{moment(job.start_timestamp).format('h:mm A')}</Text>
              )}
            </View>
            <Text style={styles.dateSeparator}>~</Text>
            <View style={styles.dateItem}>
              <Text style={styles.dateMain}>{moment(job.end_timestamp).format('MMMM Do')}</Text>
              {!isFullTime && (
                <Text style={styles.dateTime}>{moment(job.end_timestamp).format('h:mm A')}</Text>
              )}
            </View>
          </View>
        ) : (
          <View>
            <Text style={styles.dateMain}>{moment(job.start_timestamp).format('dddd, MMMM Do YYYY')}</Text>
            {!isFullTime && (
              <Text style={styles.dateTime}>
                {moment(job.start_timestamp).format('h:mm A')} ~ {moment(job.end_timestamp).format('h:mm A')}
              </Text>
            )}
          </View>
        )}
      </View>

      {/* Notes Section */}
      {job.note && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Additional Notes</Text>
          <Text style={styles.descriptionText}>{job.note}</Text>
        </View>
      )}

      {/* Facility Information */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Facility Information</Text>
        <View style={styles.facilityInfo}>
          <View style={[styles.facilityLogo, { backgroundColor: getColorFromName(job.clinic?.name) }]}>
            <Text style={styles.facilityLogoText}>{job.clinic?.name?.[0] || 'C'}</Text>
          </View>
          <View style={styles.facilityDetails}>
            <Text style={styles.facilityName}>{job.clinic?.name}</Text>
            <Text style={styles.facilityAddress}>{job.clinic?.address}</Text>
            <Text style={styles.facilityAddress}>{job.clinic?.city || job.city || clinicCity}</Text>
            <Text style={styles.facilityAddress}>{job.clinic?.postcode} {job.clinic?.state || job.state || clinicState}</Text>
          </View>
        </View>

        {/* Map Links */}
        {job.clinic?.latitude && job.clinic?.longitude && (
          <View style={styles.mapButtons}>
            <TouchableOpacity
              style={styles.mapBtn}
              onPress={() => Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${job.clinic.latitude},${job.clinic.longitude}`)}
            >
              <Text style={styles.mapBtnText}>Google Maps</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.mapBtn}
              onPress={() => Linking.openURL(`https://waze.com/ul?ll=${job.clinic.latitude},${job.clinic.longitude}&navigate=yes`)}
            >
              <Text style={styles.mapBtnText}>Waze</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      <View style={{ height: 24 }} />
    </ScrollView>
  );
};

// Helper function
const getColorFromName = (name) => {
  const colors = ['#fce7f3', '#dbeafe', '#dcfce7', '#fef3c7', '#f3e8ff'];
  return colors[name ? name.charCodeAt(0) % colors.length : 0];
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  errorText: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  // Job Header
  jobHeader: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  companyLogo: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  companyLogoText: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  jobHeaderInfo: {
    flex: 1,
  },
  urgentLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: webColors.primary,
    marginBottom: 4,
  },
  companyName: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.primary,
    marginBottom: 4,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  subtitlePill: {
    alignSelf: 'flex-start',
    backgroundColor: webColors.primaryLight,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 6,
  },
  subtitlePillText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.primary,
  },
  jobMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  metaText: {
    fontSize: 12,
    color: webColors.textMuted,
  },
  metaDot: {
    fontSize: 12,
    color: webColors.textMuted,
  },
  // Tags
  tagsRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  jobTypeTag: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: webColors.primaryLight,
    borderRadius: 6,
  },
  jobTypeTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.primary,
  },
  spayTag: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#fee2e2',
    borderRadius: 6,
  },
  spayTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FC6767',
  },
  cashTag: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
  },
  cashTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
  },
  // Section
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 12,
  },
  descriptionText: {
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
  },
  // Earnings
  salaryText: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  earningsHighlight: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 6,
  },
  earningsTotal: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginBottom: 10,
  },
  paymentTag: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 6,
    marginBottom: 10,
  },
  paymentTagText: {
    fontSize: 11,
    fontWeight: '700',
  },
  paymentNote: {
    fontSize: 13,
    color: webColors.textMuted,
    lineHeight: 20,
  },
  // Expertise
  expertiseGroup: {
    marginBottom: 14,
  },
  expertiseLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: webColors.textMuted,
    marginBottom: 8,
    letterSpacing: 0.5,
  },
  listItem: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 6,
  },
  listBullet: {
    fontSize: 14,
    color: webColors.primary,
  },
  listText: {
    flex: 1,
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 20,
  },
  // Date
  dateRange: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  dateItem: {},
  dateMain: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  dateTime: {
    fontSize: 13,
    color: webColors.textSecondary,
  },
  dateSeparator: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  // Facility
  facilityInfo: {
    flexDirection: 'row',
    gap: 14,
    marginBottom: 12,
  },
  facilityLogo: {
    width: 44,
    height: 44,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  facilityLogoText: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  facilityDetails: {
    flex: 1,
  },
  facilityName: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  facilityAddress: {
    fontSize: 13,
    color: webColors.textMuted,
    lineHeight: 18,
  },
  // Map Buttons
  mapButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  mapBtn: {
    flex: 1,
    paddingVertical: 10,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: webColors.border,
  },
  mapBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  // ==================== MOBILE VARIANTS ====================
  jobMetaMobile: {
    flexWrap: 'wrap',
  },
  dateRangeMobile: {
    flexWrap: 'wrap',
    gap: 8,
  },
});

export default DetailsContent;
